import * as React from "react";
import { useState, useContext } from "react";
import { makeStyles, Theme, createStyles } from "@mui/styles";
import {
  FormControlLabel,
  FormControl,
  FormLabel,
  FormGroup,
  InputLabel,
  FormHelperText,
  Switch,
} from "@mui/material";
import {ValueContext} from '~common/components/form-def-field-fc';
import Poptart from "~common/components/poptart";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    formDefField: {
      width: "calc(100% - 16px)"
    },
    formDefFieldWarn: {
      width: "100%",
      "& div": {
        "&::after": {
          border: "1px solid #ffe002 !important",
          transform: "scaleX(1)",
        },
      },
    },
    formDefFieldHistory: {
      width: "100%",
      "& div": {
        "&::after": {
          border: "1px solid #1614b3 !important",
          transform: "scaleX(1)",
        },
      },
    },
    compactLabel: {
      fontSize: '0.75rem !important',
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis"
    },
  })
);

interface FormControlProps {
  children: any;
  fieldDefinition: any;
  readOnlyOverride?: boolean; 
  error?: any;
  fieldControlMessage?: string;
  fieldControlClass?: string;
  underFieldRenderer?: any;
  suppressLabel?: boolean;
  policyMessages?: any;
}

export const ICGFormControl: React.FC<FormControlProps> = ({
  children,
  fieldDefinition,
  policyMessages,
  fieldControlMessage,
  fieldControlClass,
  readOnlyOverride,
  suppressLabel,
  underFieldRenderer
}) => {
  const classes = useStyles();    

  // Setting the inital value and setValue hook based on the context passed in from form def field, instead of useState
  // setFieldVal will pass the value up to the parent component that houses the context.

  var mode = fieldDefinition.mode;
  var label = fieldDefinition.title;
  var formDefClass = classes.formDefField;
  var error = false
  var parentContext = useContext(ValueContext);
  const [fieldValue, setFieldValue] = parentContext;

  var localHelperText = fieldDefinition.helpText;
  if (mode == "Display") {
    localHelperText = "";
  }

  // First Check that there are policy messages to display those
  if (policyMessages && policyMessages.length > 0) {
    
    localHelperText = policyMessages[0].title;
    
    switch (policyMessages[0].severity) {
      case 2:
        error = true;
        break;
      case 1:
        formDefClass = classes.formDefFieldWarn;
        break;
      case 4:
        formDefClass = classes.formDefFieldHistory;
        break
    }
    
  }

  // Then, if there are local messages from the field controller (like paste errors, etc), display those instead of policy messages, as these will be more up to date
  if (fieldControlMessage) {
    localHelperText = fieldControlMessage;
    switch (fieldControlClass) {
      case "error":
        error = true;
        break;
      case "warning":
        formDefClass = classes.formDefFieldWarn;
        break;
      default:
        break
    }
  }

  const onCopy = (event: any) => {
    event.stopPropagation();
    navigator.clipboard.writeText(fieldValue).then(
      function () {
        /* clipboard successfully set */

      },
      function () {
        /* clipboard write failed */

      }
    );
  };

  return (
    <FormControl
      disabled={mode == "Read Only" || readOnlyOverride}
      required={mode == "Required"}
      error={error}
      style={fieldDefinition.styleAttributes}
      className={formDefClass}>
      {!suppressLabel && <FormLabel className={classes.compactLabel}>

        {/* Actual Text Value of the Title */}
        {label}

        {/* Render the Key Field icon if the mode is Key field */}
        {fieldDefinition.mode == "Key Field" && (
          <i
            style={{ color: "#CD5C5C", marginLeft: 5 }}
            className="fa fa-key"
          ></i>
        )}
        {fieldDefinition.isCopyable && (
          <i
            style={{cursor:'pointer', marginLeft: 5 }}
            className="fal fa-copy"
            onClick={onCopy}
          ></i>
        )}

        {/* Render the popper for field info it is present */}
        {fieldDefinition.fieldInfo && fieldDefinition.fieldInfo.content && (
        <div style={{ display: "inline-block" }}>
            <Poptart
              click={true}
              className="form-def-field-info"
              placement={fieldDefinition.fieldInfo.popperPlacement || "top"}
              icon={`fa ${fieldDefinition.fieldInfo.icon || "fa-question-circle"}`}
              error={error}
              timeout={350}
              iconStyle={{
                display: "inline-block",
                marginLeft: 5,
              }}
            >
              <div
                style={{ width: fieldDefinition.fieldInfo.popperWidth, padding: 5 }}
                dangerouslySetInnerHTML={{ __html: fieldDefinition.fieldInfo.content }}
              ></div>
            </Poptart>
          </div>)}

      </FormLabel>}
      <FormGroup>
        {children}
      </FormGroup>
      {localHelperText && (
        <FormHelperText>{localHelperText}</FormHelperText>
      )}
      {underFieldRenderer}
    </FormControl>
  );
};

export default ICGFormControl;
